import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ClipboardDocumentIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ErrorsList } from 'src/components/errors'
import { Input } from 'src/components/input'
import { PhoneNumberInput } from 'src/components/phone-number-input'
import { Modal } from 'src/components/modal'
import {
  QuickAddGuestForm,
  QuickAddGuestFormInputs,
  TQuickAddGuestForm,
  reservationApi,
  reservationQueries,
} from 'src/services/reservations'

type Props = {
  reservationId: string
  isOpen: boolean
  onClose: () => void
  /** Copies the self-fill survey link so the guest fills it in themselves. */
  onCopySurveyLink: () => void
  isCopyingSurveyLink?: boolean
}

const LANGUAGES = ['es', 'en'] as const

const QuickAddGuestFields = ({
  reservationId,
  onClose,
}: Pick<Props, 'reservationId' | 'onClose'>) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { form, createFormFieldChangeHandler, overrideForm, validate } =
    useTnForm<TQuickAddGuestForm>()
  const { mutate, isPending, error } = useMutation({
    mutationFn: reservationApi.csc.quickAddGuest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
      overrideForm(new QuickAddGuestForm() as TQuickAddGuestForm)
      onClose()
    },
  })

  const submit = () => {
    // The hook's validate re-renders, so the field errors actually show.
    form.validate()
    validate()
    if (!form.isValid) return
    mutate({
      reservationId,
      firstName: form.firstName.value ?? '',
      lastName: form.lastName.value ?? '',
      phoneNumber: form.phoneNumber.value || undefined,
      email: form.email.value || undefined,
      preferredLanguage: form.preferredLanguage.value || undefined,
    })
  }

  const nameField = (name: 'firstName' | 'lastName') => (
    <div>
      <Input
        label={form[name].label}
        placeholder={form[name].placeholder}
        value={form[name].value ?? ''}
        onChange={(e) => createFormFieldChangeHandler(form[name])(e.target.value)}
        data-testid={`quick-add-${name}`}
      />
      <ErrorsList errors={form[name].errors} />
    </div>
  )
  return (
    <div className="flex flex-col gap-3" data-testid="quick-add-guest-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {nameField('firstName')}
        {nameField('lastName')}
      </div>
      <div>
        <PhoneNumberInput
          label={form.phoneNumber.label}
          placeholder={form.phoneNumber.placeholder}
          value={form.phoneNumber.value || undefined}
          onChange={(value) => createFormFieldChangeHandler(form.phoneNumber)(value ?? '')}
          data-testid="quick-add-phoneNumber"
        />
        <ErrorsList errors={form.phoneNumber.errors} />
      </div>
      <div>
        <Input
          label={form.email.label}
          placeholder={form.email.placeholder}
          type="email"
          value={form.email.value ?? ''}
          onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
          data-testid="quick-add-email"
        />
        <ErrorsList errors={form.email.errors} />
      </div>
      <label className="flex flex-col text-sm font-medium text-primary">
        <span className="mb-1">{form.preferredLanguage.label}</span>
        <select
          value={form.preferredLanguage.value ?? 'es'}
          onChange={(e) => createFormFieldChangeHandler(form.preferredLanguage)(e.target.value)}
          className="w-full rounded-md border bg-surface p-2 font-normal text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong"
          data-testid="quick-add-preferredLanguage"
        >
          {LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {t(lang === 'en' ? 'language.englishName' : 'language.spanishName')}
            </option>
          ))}
        </select>
      </label>
      {error && <p className="text-sm text-error">{t('quickAddGuest.error')}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={submit} isLoading={isPending}>
          {t('quickAddGuest.submit')}
        </Button>
      </div>
    </div>
  )
}

export const QuickAddGuestModal = ({
  reservationId,
  isOpen,
  onClose,
  onCopySurveyLink,
  isCopyingSurveyLink,
}: Props) => {
  const { t } = useTranslation()
  return (
    <Modal title={t('quickAddGuest.title')} isOpen={isOpen} onClose={onClose}>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Button
          variant="secondary"
          onClick={onCopySurveyLink}
          isLoading={isCopyingSurveyLink}
          className="flex-1 justify-center"
          data-testid="generate-guest-link-button"
        >
          <ClipboardDocumentIcon className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('quickAddGuest.sendSurveyLink')}
        </Button>
        <Link
          to={`/reservations/${reservationId}/guests`}
          className="flex-1"
          data-testid="fill-survey-now-link"
        >
          <Button variant="secondary" className="w-full justify-center">
            <DocumentTextIcon className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('quickAddGuest.fillSurveyNow')}
          </Button>
        </Link>
      </div>
      <p className="mb-3 border-t border-border pt-3 text-sm text-text-secondary">
        {t('quickAddGuest.orContactOnly')}
      </p>
      <FormProvider<QuickAddGuestFormInputs> formClass={QuickAddGuestForm}>
        <QuickAddGuestFields reservationId={reservationId} onClose={onClose} />
      </FormProvider>
    </Modal>
  )
}
